/**
 * ScrollbackToggleVanilla - A toggle button for enabling/disabling scrollback persistence.
 * Placed in the header status-group area.
 * Gray = disabled, Blue = enabled.
 */

export interface ScrollbackToggleVanillaOptions {
  container: HTMLElement;
  onToggle: (enabled: boolean) => void;
}

const ICON_SCROLL = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 12h-5"/><path d="M15 8h-5"/><path d="M19 17V5a2 2 0 0 0-2-2H4"/><path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2"/></svg>';

export class ScrollbackToggleVanilla {
  private container: HTMLElement;
  private onToggle: (enabled: boolean) => void;
  private enabled: boolean = false;
  private sessionId: string | null = null;

  constructor(options: ScrollbackToggleVanillaOptions) {
    this.container = options.container;
    this.onToggle = options.onToggle;
    this.setupEventListeners();
    this.render();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.render();
  }

  setSessionId(sessionId: string | null): void {
    this.sessionId = sessionId;
    this.render();
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  private render(): void {
    const stateClass = this.enabled ? 'sb-toggle-on' : 'sb-toggle-off';
    const title = this.enabled ? 'Scrollback persistence: ON' : 'Scrollback persistence: OFF';
    const disabled = this.sessionId ? '' : ' disabled';

    this.container.innerHTML = `
      <button class="pill sb-toggle ${stateClass}" data-action="toggle-scrollback" title="${title}" aria-label="${title}" aria-pressed="${this.enabled}"${disabled}>
        ${ICON_SCROLL}
        <span class="sb-toggle-text">${this.enabled ? 'Scrollback' : 'Scrollback'}</span>
      </button>
    `;
  }

  private setupEventListeners(): void {
    this.container.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-action="toggle-scrollback"]')) {
        if (!this.sessionId) return;
        this.enabled = !this.enabled;
        this.render();
        this.onToggle(this.enabled);
      }
    });
  }

  dispose(): void {
    this.container.innerHTML = '';
  }
}
